import { List, Map } from 'immutable';
import { constants as logConstants } from '@saucelabs/sl-component-log';

import * as constants from './../constants';
import { ANDROID_CHART_LAYOUT, IOS_CHART_LAYOUT, IOS_CHART_LAYOUT_LEGACY, DEFAULT_X_MAX_AMOUNT } from './../vitalsChartLayouts';

const DEFAULT_UPDATE_INTERVAL = 1000;
// Backend service reference: https://github.com/saucelabs/rdc-toolkit/blob/2.4.42/app/src/main/java/org/testobject/android/toolkit/vitals/VitalsReporter.java
const CSV_HEADERS_ANDROID = 'unix_epoch_milliseconds,device_local_time,cpu_total,cpu_user,cpu_kernel,n_processors,n_threads,memory_size_kb,memory_resident_kb,memory_shared_kb,network_wifi_receive_b,network_wifi_sent_b,network_mobile_receive_b,network_mobile_sent_b';
// Backend service reference: https://github.com/saucelabs/rdc-pool/blob/2019.09.23.10.38/org.saucelabs.pool/core/src/main/java/org/saucelabs/pool/core/ios/devicevitals/VitalsReporter.java#L18
const CSV_HEADERS_IOS = 'unix_epoch_milliseconds,device_local_time,cpu_total,cpu_process,n_processors,n_threads,process_memory_used_b,net_bytes_in,net_bytes_out,net_packets_in,net_packets_out,disk_write_ops,disk_bytes_written';

// - Adds new data points in the respective chart format
// - Removes all data that will not be rendered anymore based on DEFAULT_X_MAX_AMOUNT
const updateChartsData = (chartsData, layout, csvEntry, prevCSVEntry = null) => {
  /* eslint-disable camelcase, comma-style */
  switch (layout) {
    case constants.MOBILE_VITALS_LAYOUTS.ANDROID: {
      const [
        , // unix_epoch_milliseconds
        device_local_time,
        cpu_total,
        cpu_user,
        cpu_kernel,
        , // n_processors
        n_threads,
        memory_size_kb,
        memory_resident_kb,
        memory_shared_kb,
        network_wifi_receive_b,
        network_wifi_sent_b,
        network_mobile_receive_b,
        network_mobile_sent_b,
      ] = csvEntry.split(',');
      return chartsData.withMutations((mutableChartsData) => {
        mutableChartsData.setIn(
          [0, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([0, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              cpu_total,
              cpu_user,
              cpu_kernel,
            },
          ],
        );
        mutableChartsData.setIn(
          [0, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([0, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            { timestamp: device_local_time, n_threads },
          ],
        );
        mutableChartsData.setIn(
          [1, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([1, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              memory_size_kb,
              memory_resident_kb,
              memory_shared_kb,
            },
          ],
        );
        mutableChartsData.setIn(
          [2, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([2, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              network_wifi_receive_b,
              network_wifi_sent_b,
            },
          ],
        );
        mutableChartsData.setIn(
          [2, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([2, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              network_mobile_receive_b,
              network_mobile_sent_b,
            },
          ],
        );
      });
    }
    case constants.MOBILE_VITALS_LAYOUTS.IOS: {
      // IOS chart data is based on message pairs
      const [
        , // unix_epoch_milliseconds
        device_local_time,
        cpu_total,
        , // cpu_process
        , // n_processors
        , // n_threads
        , // process_memory_used_b
        net_bytes_in,
        net_bytes_out,
        net_packets_in,
        net_packets_out,
        disk_write_ops,
        disk_bytes_written,
      ] = prevCSVEntry.split(',');
      const [
        , // unix_epoch_milliseconds
        , // device_local_time,
        , // cpu_total,
        cpu_process,
        , // n_processors
        n_threads,
        process_memory_used_b,
        , // net_bytes_in
        , // net_bytes_out
        , // net_packets_in
        , // net_packets_out
        , // disk_write_ops
        disk_bytes_written_process, // originally called "disk_bytes_written"
      ] = csvEntry.split(',');

      // if cpu_process is empty this is not the second message of a message pair, so do nothing
      if (!cpu_process) {
        return chartsData;
      }
      // Otherwise create the entry based on the last 2 messages
      return chartsData.withMutations((mutableChartsData) => {
        mutableChartsData.setIn(
          [0, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([0, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              cpu_total,
              cpu_process,
            },
          ],
        );
        mutableChartsData.setIn(
          [0, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([0, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            { timestamp: device_local_time, n_threads },
          ],
        );
        mutableChartsData.setIn(
          [1, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([1, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              process_memory_used_b,
            },
          ],
        );
        mutableChartsData.setIn(
          [2, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([2, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              net_bytes_in,
              net_bytes_out,
            },
          ],
        );
        mutableChartsData.setIn(
          [2, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([2, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              net_packets_in,
              net_packets_out,
            },
          ],
        );
        mutableChartsData.setIn(
          [3, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([3, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              disk_write_ops,
            },
          ],
        );
        mutableChartsData.setIn(
          [3, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([3, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              disk_bytes_written,
              disk_bytes_written_process,
            },
          ],
        );
      });
    }
    case constants.MOBILE_VITALS_LAYOUTS.IOS_LEGACY: {
      const [
        , // unix_epoch_milliseconds
        device_local_time,
        cpu_total,
        cpu_process,
        , // n_processors
        n_threads,
        process_memory_used_b,
        net_bytes_in,
        net_bytes_out,
        net_packets_in,
        net_packets_out,
        disk_write_ops,
        disk_bytes_written,
      ] = csvEntry.split(',');
      return chartsData.withMutations((mutableChartsData) => {
        mutableChartsData.setIn(
          [0, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([0, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              cpu_total,
              cpu_process,
            },
          ],
        );
        mutableChartsData.setIn(
          [0, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([0, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            { timestamp: device_local_time, n_threads },
          ],
        );
        mutableChartsData.setIn(
          [1, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([1, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              process_memory_used_b,
            },
          ],
        );
        mutableChartsData.setIn(
          [2, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([2, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              net_bytes_in,
              net_bytes_out,
            },
          ],
        );
        mutableChartsData.setIn(
          [2, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([2, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              net_packets_in,
              net_packets_out,
            },
          ],
        );
        mutableChartsData.setIn(
          [3, 'charts', 0, 'data'],
          [
            ...chartsData.getIn([3, 'charts', 0, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              disk_write_ops,
            },
          ],
        );
        mutableChartsData.setIn(
          [3, 'charts', 1, 'data'],
          [
            ...chartsData.getIn([3, 'charts', 1, 'data']).slice(-DEFAULT_X_MAX_AMOUNT),
            {
              timestamp: device_local_time,
              disk_bytes_written,
            },
          ],
        );
      });
    }
    default:
      return chartsData;
  }
  /* eslint-enable camelcase, comma-style */
};

const initialState = new Map({
  active: false,
  fullWidthLayout: false,
  graphHeight: logConstants.GRAPH_HEIGHTS.HEIGHT_M,
  layout: '',
  csvData: new List(),
  chartsData: new List(),
  visibleChartsData: new List(),
  chartUpdateInterval: DEFAULT_UPDATE_INTERVAL,
});

const mobileVitalsReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case constants.MOBILE_VITALS_SHOW:
      return state.set('active', payload);
    case constants.MOBILE_LOG_SHOW:
      if (payload) {
        return state.set('active', false);
      }
      return state;

    case constants.MOBILE_VITALS_GRAPH_HEIGHT_CHANGE:
      return state.set('graphHeight', payload);
    case constants.MOBILE_VITALS_LAYOUT_FULLWIDTH_CHANGE:
      return state.set('fullWidthLayout', payload);

    case constants.MOBILE_VITALS_ENTRY_ADD: {
      const prevCSVData = state.get('csvData');
      const prevChartsData = state.get('chartsData');
      const layout = state.get('layout');

      if (prevCSVData.size === 0) {
        // First CSV message containing the headers
        // Set the chart Android layout if header matches
        if (payload.startsWith(CSV_HEADERS_ANDROID)) {
          return state.withMutations((mutableState) => {
            mutableState
              .set('layout', constants.MOBILE_VITALS_LAYOUTS.ANDROID)
              .set('chartsData', ANDROID_CHART_LAYOUT)
              .set('csvData', prevCSVData.push(payload));
          });
        }
        // Otherwise just store the data
        return state.set('csvData', prevCSVData.push(payload));
      }

      if (prevCSVData.size === 1 && !layout) {
        if (prevCSVData.get(0).startsWith(CSV_HEADERS_IOS)) {
          // New iOS vitals don't have the full data in every message (they come in message pairs)
          const payloadValues = payload.split(',');
          if (!payloadValues[3]) { // Use the default iOS layout if this value is empty
            return state.withMutations((mutableState) => {
              mutableState
                .set('layout', constants.MOBILE_VITALS_LAYOUTS.IOS)
                .set('chartsData', IOS_CHART_LAYOUT)
                .set('csvData', prevCSVData.push(payload));
            });
          }
          // Otherwise use the iOS legacy layout that use standalone messages
          return state.withMutations((mutableState) => {
            mutableState
              .set('layout', constants.MOBILE_VITALS_LAYOUTS.IOS_LEGACY)
              .set('chartsData', IOS_CHART_LAYOUT_LEGACY)
              .set('csvData', prevCSVData.push(payload));
          });
        }
      }

      return state.withMutations((mutableState) => {
        mutableState
          .set('chartsData', updateChartsData(prevChartsData, layout, payload, prevCSVData.last()))
          .set('csvData', prevCSVData.push(payload));
      });
    }

    case constants.MOBILE_VITALS_UPDATE_VISIBLE_DATA: {
      return state.set('visibleChartsData', state.get('chartsData'));
    }

    case constants.MOBILE_SESSION_STOP.SUCCESS:
      return state.withMutations((mutableState) => (
        mutableState
          .set('active', false)
          .set('csvData', new List())
          .set('chartsData', new List())
          .set('layout', '')
          .set('visibleChartsData', new List())
      ));

    default:
      return state;
  }
};

export default mobileVitalsReducer;
export { initialState, updateChartsData };
